import { useMemo } from 'react';

import { useStoreContext } from 'features/store';

import usePosVendor from 'hooks/menu/use-pos-vendor';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUnavailableLogger } from 'state/loyalty/hooks/use-loyalty-evaluation-result-logger';
import { getWalmartSeasonalConfigOfferIds } from 'state/loyalty/hooks/utils/walmart';
import { appOfferToLoyaltyOffer, isAppOffer } from 'state/loyalty/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';

import { useLoyaltyWalmart } from '../loyalty-partners/walmart/use-loyalty-walmart';

import { useGetCmsAndLoyaltyOffers } from './use-get-cms-and-loyalty-offers';
import { useReduceOffers } from './use-offers-reducer';
import { isLegacyMenuOfferEntityAvailable, isOneMenuOfferEntityAvailable } from './utils';

export const useLoyaltyBaseOffers = () => {
  // Loyalty offers
  const {
    sanityOfferListItems,
    appPersonalizedOffers,
    appPersonalizedOfferGroups,
    engineOfferListItems,
    offersLoading,
    refreshOffers,
    refreshOffersLoading,
  } = useGetCmsAndLoyaltyOffers();

  const { offerListItems, offersInCart, reductionLoading, reprocessOffers } = useReduceOffers({
    sanityOfferListItems,
    appPersonalizedOffers,
    appPersonalizedOfferGroups,
    engineOfferListItems,
    offersLoading,
    refreshOffersLoading,
  });

  // Menu Availability
  const { prices } = useStoreContext();
  const { vendor } = usePosVendor();
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { indexedMenu } = useUnifiedMenuContext();
  const { posServiceMode } = useMenuQueryOptions();
  const unavailableLogger = useLoyaltyUnavailableLogger('useLoyaltyBaseOffers');

  // Walmart
  const { data: loyaltyWalmart, loading: isLoyaltyWalmartLoading } = useLoyaltyWalmart();
  const showWalmartSeasonalPromo = useFlag(LaunchDarklyFlag.SHOW_WALMART_SEASONAL_PROMO);

  // Return values
  const loading =
    offersLoading || reductionLoading || refreshOffersLoading || isLoyaltyWalmartLoading;

  const filteredOffers = useMemo(() => {
    if (loading) {
      return offerListItems;
    }

    const walmartSeasonalOffers = getWalmartSeasonalConfigOfferIds(loyaltyWalmart);

    return offerListItems.filter(offerListItem => {
      if (!isAppOffer(offerListItem)) {
        return true;
      }

      // Discard seasonal promo if flag is OFF
      if (!showWalmartSeasonalPromo && walmartSeasonalOffers.includes(offerListItem.cmsId)) {
        return false;
      }
      const isAvailable = isUnifiedMenuEnabled
        ? isOneMenuOfferEntityAvailable(offerListItem, indexedMenu, posServiceMode)
        : isLegacyMenuOfferEntityAvailable(offerListItem, prices, vendor);

      // Discard unavailable offer
      if (!isAvailable) {
        unavailableLogger({
          loyaltyItem: appOfferToLoyaltyOffer(offerListItem),
          loyaltyItemType: 'Offer',
        });
        return false;
      }

      return true;
    });
  }, [
    loading,
    loyaltyWalmart,
    offerListItems,
    showWalmartSeasonalPromo,
    isUnifiedMenuEnabled,
    indexedMenu,
    posServiceMode,
    prices,
    vendor,
    unavailableLogger,
  ]);

  return {
    offerListItems: filteredOffers,
    offersInCart,
    reprocessOffers,
    refreshOffers,
    loading,
    baseOffersLoading: offersLoading,
  };
};
